import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  IconButton, 
  Typography, 
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  alpha,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

// Google logo SVG component
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Login = ({ open, onClose, message, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { signInWithGoogle } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Reset error state when dialog closes or unmounts
  React.useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      console.log('Google sign-in successful:', user);
      
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(user);
      }
      
      setTimeout(() => {
        handleClose();
      }, 30);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog 
          open={open} 
          onClose={handleClose}
          TransitionComponent={Fade}
          TransitionProps={{
            timeout: 300
          }}
          PaperProps={{
            component: motion.div,
            initial: { opacity: 0, y: 20, scale: 0.98 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: 20, scale: 0.98 },
            transition: { duration: 0.3 },
            sx: {
              borderRadius: { xs: '10px', sm: '12px' },
              width: { xs: '90%', sm: '400px' },
              maxWidth: '95vw',
              margin: { xs: 1.5, sm: 2 },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease-in-out',
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px) scale(0.98)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0) scale(1)'
                }
              }
            },
          }}
          sx={{
            '& .MuiBackdrop-root': {
              backdropFilter: 'blur(5px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
            '& .MuiDialog-container': {
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
              paddingTop: { xs: '5vh', sm: '10vh' },
            },
          }}
        >
          <Box sx={{ 
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(94, 23, 235, 0.03), rgba(94, 23, 235, 0.06))',
            pt: { xs: 2, sm: 2.5 },
            pb: { xs: 1.5, sm: 2 },
            px: { xs: 1.5, sm: 2 },
          }}>
            <DialogTitle sx={{ 
              textAlign: 'center', 
              p: 0,
              fontSize: { xs: '1.25rem', sm: '1.4rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #5E17EB, #8352FF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}>
              Welcome to roamzap
            </DialogTitle>
            
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.08),
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                px: { xs: 1, sm: 3 },
                mb: 0
              }}
            >
              {message || 'Sign in to unlock personalized trip recommendations, save your itineraries, and sync across devices.'}
            </Typography>
          </Box>

          <DialogContent sx={{ 
            px: { xs: 1.5, sm: 2 }, 
            py: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <motion.div
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%' }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<GoogleLogo />}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                fullWidth
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: '10px',
                  borderWidth: 1.5,
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'text.primary',
                  fontWeight: 600,
                  '&:hover': {
                    borderWidth: 1.5,
                    borderColor: 'rgba(0, 0, 0, 0.2)',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  },
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  textTransform: 'none',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isLoading ? (
                  <CircularProgress 
                    size={24} 
                    sx={{ 
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: theme.palette.primary.main
                    }} 
                  />
                ) : (
                  'Continue with Google'
                )}
              </Button>
            </motion.div>

            <Box sx={{ 
              mt: { xs: 2, sm: 2.5 }, 
              width: '100%',
              position: 'relative'
            }}>
              <Divider sx={{
                '&::before, &::after': {
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                }
              }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    px: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  Protected by Google
                </Typography>
              </Divider>
            </Box>

            <Typography 
              variant="caption" 
              color="text.secondary" 
              align="center"
              sx={{ 
                mt: 1.5,
                fontSize: '0.65rem',
                maxWidth: '85%',
                lineHeight: 1.4
              }}
            >
              By continuing, you agree to our{' '}
              <Typography
                component="a"
                href="/terms"
                variant="caption"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Terms of Service
              </Typography>
              {' '}and{' '}
              <Typography
                component="a"
                href="/privacy"
                variant="caption"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Privacy Policy
              </Typography>
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default Login; 